// src/App.js
import React, { useEffect, useContext, useState } from 'react'; // Import useEffect, useContext, and useState
import { ErrorBoundary } from 'react-error-boundary';
import { BrowserRouter, Routes, Route, useNavigate, Outlet, useLocation, useParams } from 'react-router-dom'; // Import useNavigate
import SessionTracker from './components/topbar/SessionTracker';
import './App.css';
import { useSiteLanguage } from './hooks/useSiteLanguage';
import Home from './pages/Home';
import About from './pages/About/About';
import Subscription from './pages/account/Subscription';
import SubscriptionSuccess from './pages/account/SubscriptionSuccess';
import Login from './pages/account/Login';
import TopBar from './components/topbar/TopBar';
//import { UserContext } from "./context/UserContext";
import { UserContext } from './context/UserContext'; // Import UserContext
import { CartProvider } from './context/CartContext'; // Import CartProvider
import { MapProvider } from './context/MapContext';
import { NotificationProvider } from './context/NotificationContext'; // Import NotificationProvider
import { BannerProvider } from './context/BannerContext'; // Import BannerProvider
import { ConfigProvider, ConfigContext } from './context/ConfigContext'; // Import ConfigProvider and Context
import { CookieConsentProvider, useCookieConsent } from './context/CookieConsentContext';
import { PageMetadataProvider } from './context/PageMetadataContext';
import { ReportPollingProvider } from './context/ReportPollingContext'; // Import ReportPollingProvider
import GlobalNotificationArea from './components/notifications/GlobalNotificationArea'; // Import GlobalNotificationArea
import Snackbar from './components/notifications/Snackbar'; // Import Snackbar
import GlobalBannerArea from './components/banner/GlobalBannerArea';
import GlobalAnnouncement from './components/topbar/GlobalAnnouncement';
import Properties from './pages/Properties';
import ProtectedRoute from './components/ProtectedRoute';
import AdminRoute from './components/AdminRoute';
import SellerRoute from './components/SellerRoute'; // Import SellerRoute
//import NoPage from './pages/NoPage';
import NoPage from './pages/NoPage';
import Preferences from './pages/account/Preferences';
import MapComponent from './pages/Map';
import TestGoogleAddressSearch from './pages/TestGoogleAddressSearch';
import TestOpenStreetMapAddressSearch from './pages/TestOpenStreetMapAddressSearch';
import UserManagement from './pages/Admin/UserManagement';
import AnnouncementPage from './pages/Admin/AnnouncementPage';
import AdminEmails from './pages/Admin/AdminEmails';
import Messages from './pages/Admin/Messages';
import VerifyEmailPage from './pages/account/VerifyEmailPage';
import { initApiClient } from './utils/apiClient'; // Import initApiClient
import ForgotPassword from './pages/account/ForgotPassword'; // Import ForgotPassword
import ResetPassword from './pages/account/ResetPassword'; // Import ResetPassword
import ChangePassword from './pages/account/ChangePassword'; // Import ChangePassword
import PaymentSelection from './pages/PaymentSelection';
import DeleteAccount from './pages/account/DeleteAccount'; // Import DeleteAccount
import ContactUs from './pages/ContactUs';
import TermsOfUse from './pages/legal/TermsOfUse';
import PrivacyPolicy from './pages/legal/PrivacyPolicy';
import CancellationPolicy from './pages/legal/CancellationPolicy';
import ProviderTermsOfUse from './pages/legal/ProviderTermsOfUse';
import CookiePolicy from './pages/legal/CookiePolicy';
import MyOrders from './pages/MyOrders';
import StatisticsPage from './pages/StatisticsPage';
import MyInquiriesPage from './pages/MyInquiriesPage';
import CartPage from './pages/CartPage';
import CheckoutPage from './pages/CheckoutPage';
import AuthCallbackPage from './pages/account/AuthCallbackPage';
import ImpersonationBanner from './components/ImpersonationBanner';
import MaintenanceOverlay from './components/MaintenanceOverlay';
import EventViewer from './pages/Admin/EventViewer';
import AdminTaskRunner from './pages/Admin/AdminTaskRunner';
import LogViewer from './pages/Admin/LogViewer';
import PropertyPage from './pages/property/PropertyPage';
import CookieConsent from './components/CookieConsent';
import CookieSettingsModal from './components/modals/CookieSettingsModal';
import AnalyticsInitializer from './components/AnalyticsInitializer';
import { IS_PRERENDERING } from './utils/config';
import LanguageRedirector from './components/LanguageRedirector';

const HreflangLinks = () => {
  const location = useLocation();
  const { lang } = useParams();

  const supportedLangs = ['en', 'es', 'he', 'ru'];
  const basePath = lang ? location.pathname.replace(`/${lang}`, '') : location.pathname;

  const isRedirectRoute = !lang && ['/reset-password', '/verify-email', '/auth/callback'].some((p) => location.pathname.startsWith(p));
  if (isRedirectRoute) return null;

  // For prerendering, use relative URLs. For live browser, use absolute URLs.
  const domain = IS_PRERENDERING ? '' : window.location.origin;

  return (
    <>
      <link rel="alternate" hrefLang="x-default" href={`${domain}/en${basePath}`} />
      {supportedLangs.map((l) => (
        <link key={l} rel="alternate" hrefLang={l} href={`${domain}/${l}${basePath}`} />
      ))}
    </>
  );
};

// A minimal layout component that includes the TopBar
const LanguageLayout = () => {
  useSiteLanguage(); // This hook is essential for language-based routing
  const { lang } = useParams();

  // Set the lang attribute on the HTML tag for SEO and accessibility
  useEffect(() => {
    document.documentElement.lang = lang || 'en';
  }, [lang]);

  return (
    <>
      <HreflangLinks />
      <SessionTracker>
        {/* The TopBar is a candidate for causing hydration issues. */}
        <TopBar />
      </SessionTracker>
      <Outlet />
    </>
  );
};

const AppLayout = () => {
  const { user } = useContext(UserContext);
  const { shutdownStatus } = useContext(ConfigContext);
  const { isSettingsOpen, closeSettings } = useCookieConsent();

  // Show maintenance overlay for non-admins if shutdown is active
  const showMaintenanceOverlay = (shutdownStatus === 'SHUTDOWN_INITIATED' || shutdownStatus === 'SHUTDOWN_PENDING') && !user.isAdmin;

  return (
    <>
      {showMaintenanceOverlay && <MaintenanceOverlay message="Our system is currently undergoing scheduled maintenance to bring you exciting new features and improvements. We'll be back online shortly! Thank you for your patience." />}
      {/* The main app container. We apply styles to blur and disable interaction when the overlay is active. */}
      <div className="app-container" style={{ pointerEvents: showMaintenanceOverlay ? 'none' : 'auto', filter: showMaintenanceOverlay ? 'blur(4px)' : 'none' }}>
        <ImpersonationBannerRenderer />
        <GlobalBannerArea />
        <GlobalNotificationArea />
        <Snackbar />
        <ErrorBoundary FallbackComponent={() => null}>
          <GlobalAnnouncement />
        </ErrorBoundary>
        <AnalyticsInitializer />
        <CookieSettingsModal isOpen={isSettingsOpen} onClose={closeSettings} />
        <CookieConsent />
        <div className="main-layout">
          <main className="working-area">
            <Routes>
              {/* The root path now also uses LanguageLayout to determine the correct language */}
              <Route path="/" element={<LanguageLayout />}>
                <Route index element={<Home />} />
              </Route>

              {/* Routes for handling language-less paths from external links (e.g., emails) */}
              <Route path="/reset-password/:token" element={<LanguageRedirector />} />
              <Route path="/verify-email" element={<LanguageRedirector />} />
              <Route path="/auth/callback" element={<LanguageRedirector />} />

              {/* All pages are now nested under a language code */}
              <Route path="/:lang" element={<LanguageLayout />}>
                <Route index element={<Home />} />
                <Route path="about" element={<About />} />
                <Route path="login" element={<Login />} />
                <Route path="subscribe" element={<Subscription />} />
                <Route path="subscription-success" element={<SubscriptionSuccess />} />
                <Route path="contact-us" element={<ContactUs />} />
                <Route path="verify-email" element={<VerifyEmailPage />} />
                <Route path="forgot-password" element={<ForgotPassword />} />
                <Route path="reset-password/:token" element={<ResetPassword />} />
                <Route path="map" element={<MapComponent />} />
                <Route path="property/:propertyId" element={<PropertyPage />} />
                <Route path="auth/callback" element={<AuthCallbackPage />} />
                {/* Legal Pages */}
                <Route path="terms-of-use" element={<TermsOfUse />} />
                <Route path="privacy-policy" element={<PrivacyPolicy />} />
                <Route path="cancellation-policy" element={<CancellationPolicy />} />
                <Route path="provider-terms-of-use" element={<ProviderTermsOfUse />} />
                <Route path="cookie-policy" element={<CookiePolicy />} />
                <Route element={<ProtectedRoute />}>
                  <Route path="cart" element={<CartPage />} />
                  <Route path="checkout" element={<CheckoutPage />} />
                  <Route path="preferences" element={<Preferences />} />
                  <Route path="my-orders" element={<MyOrders />} />
                  <Route path="select-payment" element={<PaymentSelection />} />
                  <Route path="change-password" element={<ChangePassword />} />
                  <Route path="delete-account" element={<DeleteAccount />} />
                  {/* Seller and Admin Routes */}
                  <Route element={<SellerRoute />}>
                    <Route path="properties" element={<Properties />} />
                    <Route path="statistics" element={<StatisticsPage />} />
                    <Route path="inquiries" element={<MyInquiriesPage />} />
                  </Route>
                  {/* Admin Only Routes */}
                  <Route path="admin" element={<AdminRoute />}>
                    <Route path="user-management" element={<UserManagement />} />
                    <Route path="messages" element={<Messages />} />
                    <Route path="admin-emails" element={<AdminEmails />} />
                    <Route path="announcement" element={<AnnouncementPage />} />
                    <Route path="events" element={<EventViewer />} />
                    <Route path="tasks" element={<AdminTaskRunner />} />
                    <Route path="log-viewer" element={<LogViewer />} />
                    <Route path="testGoogleAddress" element={<TestGoogleAddressSearch />} />
                    <Route path="testOpenStreetMapAddress" element={<TestOpenStreetMapAddressSearch />} />
                  </Route>
                </Route>
                {/* Catch-all for any path under a language code that doesn't match */}
                <Route path="*" element={<NoPage />} />
              </Route>
            </Routes>
          </main>
        </div>
      </div>
    </>
  );
};

// Component to initialize the API client
const AppInitializer = () => {
  const { updateUser, updateToken } = useContext(UserContext);
  const navigate = useNavigate();

  useEffect(() => {
    initApiClient({
      updateUser,
      updateToken,
      navigate,
    });
  }, [updateUser, updateToken, navigate]);

  return null; // This component does not render anything itself
};

const ImpersonationBannerRenderer = () => {
  const { user } = useContext(UserContext);
  const [isClient, setIsClient] = useState(false);

  useEffect(() => {
    // This effect runs only on the client, after the initial render
    setIsClient(true);
  }, []);

  // On the server and during the initial client render, don't render the banner
  // to prevent a mismatch. Render it only on the client after hydration.
  if (!isClient || !user.impersonatorId) {
    return null;
  }

  return <ImpersonationBanner />;
};

function App() {
  return (
    <BrowserRouter>
      <NotificationProvider>
        <BannerProvider>
          <ConfigProvider>
            <CookieConsentProvider>
              <PageMetadataProvider>
                <AppInitializer />
                <CartProvider>
                  <ReportPollingProvider>
                    <MapProvider>
                      <AppLayout />
                    </MapProvider>
                  </ReportPollingProvider>
                </CartProvider>
              </PageMetadataProvider>
            </CookieConsentProvider>
          </ConfigProvider>
        </BannerProvider>
      </NotificationProvider>
    </BrowserRouter>
  );
}

export default App;
